import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { CallLogDialog } from "@/components/CallLogDialog";
import { FollowUpDialog } from "@/components/FollowUpDialog";
import { StatusChangeDialog } from "@/components/StatusChangeDialog";
import { EditLeadDialog } from "@/components/EditLeadDialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Phone, Mail, Globe, CalendarClock, StickyNote, GitBranch, ArrowLeft,
  Loader2, Trash2, Pencil, MapPin, Building2, Send, Instagram, Flag,
} from "lucide-react";
import * as Icons from "lucide-react";
import { cn } from "@/lib/utils";
import {
  STATUS, SOURCES, WEBSITE_STATES, LOST_REASONS, PRIORITIES, ACTIVITY_META,
  fmtEUR, fmtDateTime, labelOf,
} from "@/lib/constants";

function ActionBtn({ icon: Icon, label, onClick, href, testid, tint }) {
  const cls = "flex flex-col items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-2 py-3 text-xs font-medium hover:border-primary/40 hover:bg-accent transition-colors";
  const content = (<><Icon className={cn("h-5 w-5", tint)} /><span>{label}</span></>);
  if (href) return <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={testid} className={cls}>{content}</a>;
  return <button onClick={onClick} data-testid={testid} className={cls}>{content}</button>;
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [callOpen, setCallOpen] = useState(false);
  const [fuOpen, setFuOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const { data: lead, isLoading, isError } = useQuery({
    queryKey: ["lead", id],
    queryFn: () => api.get(`/leads/${id}`).then((r) => r.data),
  });
  const { data: activities = [] } = useQuery({
    queryKey: ["activities", id],
    queryFn: () => api.get(`/leads/${id}/activities`).then((r) => r.data),
  });

  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/leads/${id}/notes`, { description: note.trim() });
      toast.success("Notiz gespeichert");
      setNote("");
      qc.invalidateQueries();
    } catch {
      toast.error("Fehler");
    } finally {
      setSavingNote(false);
    }
  };

  const deleteLead = async () => {
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead gelöscht");
      qc.invalidateQueries();
      navigate("/leads");
    } catch {
      toast.error("Fehler beim Löschen");
    }
  };

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (isError || !lead) return <div className="p-8 text-center text-muted-foreground">Lead nicht gefunden.</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 fade-in max-w-5xl">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Zurück
      </button>

      {/* Header */}
      <div className="rounded-xl border border-border bg-card p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight" data-testid="lead-title">{lead.company}</h1>
              <StatusBadge status={lead.status} />
              {lead.priority && (() => {
                const p = PRIORITIES.find((x) => x.value === lead.priority);
                return p ? <span data-testid="lead-priority-badge" className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${p.color}`}><Flag className="h-3 w-3" />{p.label}</span> : null;
              })()}
              {lead.is_demo && <span className="text-[11px] rounded-full bg-amber-500/15 text-amber-300 px-2 py-0.5 border border-amber-500/30">DEMO</span>}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
              {lead.industry && <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{lead.industry}</span>}
              {lead.contact_person && <span>{lead.contact_person}</span>}
              {lead.district && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{lead.district}</span>}
              {lead.address && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{lead.address}</span>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted-foreground">{lead.status === "kunde" ? "Auftragswert" : "Potenzieller Wert"}</div>
            <div className="text-2xl font-bold text-sky-400">{fmtEUR(lead.status === "kunde" ? (lead.actual_value ?? lead.potential_value) : lead.potential_value)}</div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          {lead.phone && <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 text-foreground hover:text-primary"><Phone className="h-3.5 w-3.5" />{lead.phone}</a>}
          {lead.email && <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 text-foreground hover:text-primary"><Mail className="h-3.5 w-3.5" />{lead.email}</a>}
          {lead.website && <a href={lead.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-foreground hover:text-primary"><Globe className="h-3.5 w-3.5" />Website</a>}
          {lead.instagram && <a href={lead.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-foreground hover:text-primary"><Instagram className="h-3.5 w-3.5" />Instagram</a>}
          <span className="text-muted-foreground">Quelle: {labelOf(SOURCES, lead.source)}</span>
          <span className="text-muted-foreground">Website: {labelOf(WEBSITE_STATES, lead.website_state)}</span>
        </div>

        {(lead.problem || lead.notes || lead.pitch) && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {lead.problem && <div className="rounded-lg bg-accent/50 p-3 text-sm"><div className="text-xs text-muted-foreground mb-1">Problem / Chance</div>{lead.problem}</div>}
            {lead.pitch && <div className="rounded-lg bg-primary/10 border border-primary/20 p-3 text-sm"><div className="text-xs text-primary/80 mb-1">Gesprächseinstieg</div>{lead.pitch}</div>}
            {lead.notes && <div className="rounded-lg bg-accent/50 p-3 text-sm"><div className="text-xs text-muted-foreground mb-1">Notizen</div>{lead.notes}</div>}
          </div>
        )}
        {lead.status === "verloren" && lead.lost_reason && (
          <div className="mt-3 rounded-lg bg-rose-500/10 border border-rose-500/20 p-3 text-sm text-rose-300">
            Ablehnungsgrund: {labelOf(LOST_REASONS, lead.lost_reason)}
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        <ActionBtn icon={Phone} label="Anrufen" href={lead.phone ? `tel:${lead.phone}` : undefined} onClick={() => !lead.phone && toast.error("Keine Nummer")} testid="action-call" tint="text-emerald-400" />
        <ActionBtn icon={Mail} label="E-Mail" href={lead.email ? `mailto:${lead.email}` : undefined} onClick={() => !lead.email && toast.error("Keine E-Mail")} testid="action-email" tint="text-blue-400" />
        <ActionBtn icon={Globe} label="Website" href={lead.website || undefined} onClick={() => !lead.website && toast.error("Keine Website")} testid="action-website" tint="text-sky-400" />
        <ActionBtn icon={StickyNote} label="Telefonat" onClick={() => setCallOpen(true)} testid="action-calllog" tint="text-amber-400" />
        <ActionBtn icon={CalendarClock} label="Follow-up" onClick={() => setFuOpen(true)} testid="action-followup" tint="text-indigo-400" />
        <ActionBtn icon={GitBranch} label="Status" onClick={() => setStatusOpen(true)} testid="action-status" tint="text-cyan-400" />
      </div>

      <div className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-lead-btn"><Pencil className="h-3.5 w-3.5 mr-1.5" />Bearbeiten</Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="sm" className="text-rose-400 hover:text-rose-300" data-testid="delete-lead-btn"><Trash2 className="h-3.5 w-3.5 mr-1.5" />Löschen</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Lead wirklich löschen?</AlertDialogTitle>
              <AlertDialogDescription>Diese Aktion kann nicht rückgängig gemacht werden. Der Kontaktverlauf und alle Follow-ups werden ebenfalls gelöscht.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction onClick={deleteLead} data-testid="confirm-delete-btn" className="bg-rose-600 hover:bg-rose-700">Löschen</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Add note + timeline */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-lg font-semibold">Kontaktverlauf</h2>
          {activities.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Aktivitäten.</p>
          ) : (
            <ol className="relative border-l border-border pl-6 space-y-5">
              {activities.map((a) => {
                const meta = ACTIVITY_META[a.type] || ACTIVITY_META.notiz;
                const Icon = Icons[meta.icon] || Icons.Circle;
                return (
                  <li key={a.id} data-testid={`activity-${a.id}`} className="relative">
                    <span className={cn("absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full bg-card border border-border", meta.color)}>
                      <Icon className="h-3 w-3" />
                    </span>
                    <div className="text-sm">{a.description}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{meta.label} · {fmtDateTime(a.created_at)}</div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">Notiz hinzufügen</h2>
          <Textarea data-testid="quick-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="Schnelle Notiz …" />
          <Button className="w-full" onClick={addNote} disabled={savingNote} data-testid="quick-note-submit">
            {savingNote ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}Speichern
          </Button>
        </div>
      </div>

      <CallLogDialog open={callOpen} onOpenChange={setCallOpen} lead={lead} />
      <FollowUpDialog open={fuOpen} onOpenChange={setFuOpen} lead={lead} />
      <StatusChangeDialog open={statusOpen} onOpenChange={setStatusOpen} lead={lead} />
      {editOpen && <EditLeadDialog open={editOpen} onOpenChange={setEditOpen} lead={lead} />}
    </div>
  );
}
